import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { buildCollectionMap, resolveChainedBindings, scanFieldTree } from 'ContentComponents/components/cmv2/utils/bindingResolution';
import { bindingsToFieldConfigs, fieldConfigsToBindings } from 'ContentData/utils/fieldConfig';
import { useGlobalStylesCollectionData } from 'ContentComponents/components/cmv2/hooks/useGlobalStylesCollectionData';

// Stable references so `pathPrefix` only changes identity when `isInGlobalStyles` toggles.
const GS_PATH_PREFIX = ['theme'];
const EMPTY_PATH_PREFIX = [];
export function useBindingState({
  fields,
  module,
  isInGlobalStyles,
  isInNewEditor,
  initialBindings,
  onBindingsChange,
  variantFieldKeyPrefix
}) {
  const {
    hasCollection: hasCollectionFields,
    hasGlobalStylesBindings
  } = useMemo(() => scanFieldTree(fields), [fields]);
  const {
    collectionMap: gsCollectionMap,
    isLoading: gsIsLoading,
    isReady: gsIsReady
  } = useGlobalStylesCollectionData(Boolean(isInNewEditor) && !isInGlobalStyles && hasGlobalStylesBindings);
  const isGlobalStylesSource = !isInGlobalStyles && hasGlobalStylesBindings && gsIsReady;
  const bindingsRef = useRef(initialBindings ? fieldConfigsToBindings(initialBindings) : {});
  const [bindings, setBindings] = useState(
  // eslint-disable-next-line react-hooks/refs
  () => bindingsRef.current);

  // Track latest module value for the bindings-change effect (can't put module in deps
  // because it changes on every keystroke - we only want to rebuild when bindings change)
  const moduleRef = useRef(module);
  // eslint-disable-next-line react-hooks/refs
  moduleRef.current = module;
  const pathPrefix = isInGlobalStyles ? GS_PATH_PREFIX : EMPTY_PATH_PREFIX;

  // eslint-disable-next-line react-hooks/refs
  const [collectionMap, setCollectionMap] = useState(() => {
    if (isGlobalStylesSource) {
      return gsCollectionMap;
    }
    return buildCollectionMap(fields, module, pathPrefix, bindingsRef.current, variantFieldKeyPrefix);
  });

  // Rebuild when upstream data changes (field spec arrives, GS data loads).
  // module is excluded - field value edits go through rebuildCollectionMap instead.
  // bindings is excluded - binding changes go through onBindingChange instead.
  // variantFieldKeyPrefix excluded - variant switches remount via key prop (ThemeEditForm).
  useEffect(() => {
    if (isGlobalStylesSource) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCollectionMap(gsCollectionMap);
    } else if (fields.length) {
      setCollectionMap(buildCollectionMap(fields, moduleRef.current, pathPrefix, bindingsRef.current, variantFieldKeyPrefix));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, isGlobalStylesSource, gsCollectionMap, isInGlobalStyles]);
  const onBindingChange = useCallback((fieldPath, ref) => {
    const newBindings = Object.assign({}, bindingsRef.current, {
      [fieldPath]: ref
    });
    bindingsRef.current = newBindings;
    setBindings(newBindings);
    onBindingsChange === null || onBindingsChange === void 0 || onBindingsChange(bindingsToFieldConfigs(newBindings));
    if (hasCollectionFields) {
      setCollectionMap(buildCollectionMap(fields, moduleRef.current, pathPrefix, newBindings, variantFieldKeyPrefix));
    }
  }, [onBindingsChange, fields, hasCollectionFields, pathPrefix, variantFieldKeyPrefix]);

  // Explicit rebuild for field value edits — called by EditForm from onFieldChange/onFieldReset
  // with the new module data. Separate from the useEffect because module changes on every
  // keystroke and we only want to rebuild when the caller says to.
  const rebuildCollectionMap = useCallback(newModule => {
    if (hasCollectionFields) {
      setCollectionMap(buildCollectionMap(fields, newModule, pathPrefix, bindingsRef.current, variantFieldKeyPrefix));
    }
  }, [fields, hasCollectionFields, pathPrefix, variantFieldKeyPrefix]);
  const resolvedBindings = useMemo(() => resolveChainedBindings(collectionMap, bindings, pathPrefix, variantFieldKeyPrefix), [collectionMap, bindings, pathPrefix, variantFieldKeyPrefix]);
  const isGlobalStylesLoading = Boolean(isInNewEditor) && !isInGlobalStyles && hasGlobalStylesBindings && gsIsLoading;
  return {
    bindings,
    collectionMap,
    resolvedBindings,
    isGlobalStylesLoading,
    onBindingChange,
    rebuildCollectionMap,
    hasCollectionFields,
    hasGlobalStylesBindings
  };
}